/* eslint-disable react/no-unstable-nested-components */
import CalendarIcon from "@/assets/icons/image-calendar.png";
import ExpandableCard from "@/components/widgets/dashboard/ExpandableCard";
import AgendaEmailAddress from "@/domain/entities/agenda/AgendaEmailAddress";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import AgendaItemOrganizer from "@/domain/entities/agenda/AgendaItemOrganizer";
import Language from "@/domain/enums/Language";
import useGetAgendaEvents from "@/hooks/agenda/useGetAgendaEvents";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import i18n from "@/i18n";
import eventStore, { setOpenedEvent } from "@/stores/eventStore";
import { shallow, useSelector } from "@tanstack/react-store";
import { endOfWeek, format, getDay, parse, startOfWeek } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { fr } from "date-fns/locale/fr";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    Calendar,
    dateFnsLocalizer,
    EventProps,
    Views,
    type Messages,
} from "react-big-calendar";
import { useTranslation } from "react-i18next";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./styles.css";
import CalendarEventCard from "./CalendarEventCard";
import EventModal from "./eventModal/EventModal";

const locales = {
    "en-US": enUS,
    "fr-FR": fr,
};

const fnsLocalizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

type DashboardCalendarProps = {
    isExpanded: boolean;
    onExpand: () => void;
    onCollapse: () => void;
};

const DashboardCalendar = ({
    isExpanded,
    onExpand,
    onCollapse,
}: DashboardCalendarProps) => {
    const { t } = useTranslation();
    const { data: user } = useGetUserProfile();

    const [currentView, setCurrentView] = useState<
        (typeof Views)[keyof typeof Views]
    >(Views.DAY);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const hasScrolledToCurrentTime = useRef(false);

    const { startDate, endDate } = useMemo(
        () => ({
            startDate: startOfWeek(calendarDate, { weekStartsOn: 1 }),
            endDate: endOfWeek(calendarDate, { weekStartsOn: 1 }),
        }),
        [calendarDate],
    );

    const { data: agendaEvents } = useGetAgendaEvents(startDate, endDate);

    const openedEvent = useSelector(eventStore, (state) => state.openedEvent, {
        compare: shallow,
    });

    const calendarMessages: Messages<AgendaItem> = {
        date: t("dashboard.widgets.calendar.messages.date"),
        time: t("dashboard.widgets.calendar.messages.time"),
        event: t("dashboard.widgets.calendar.messages.event"),
        allDay: t("dashboard.widgets.calendar.messages.allDay"),
        week: t("dashboard.widgets.calendar.messages.week"),
        work_week: t("dashboard.widgets.calendar.messages.work_week"),
        day: t("dashboard.widgets.calendar.messages.day"),
        month: t("dashboard.widgets.calendar.messages.month"),
        previous: t("dashboard.widgets.calendar.messages.previous"),
        next: t("dashboard.widgets.calendar.messages.next"),
        yesterday: t("dashboard.widgets.calendar.messages.yesterday"),
        tomorrow: t("dashboard.widgets.calendar.messages.tomorrow"),
        today: t("dashboard.widgets.calendar.messages.today"),
        agenda: t("dashboard.widgets.calendar.messages.agenda"),
        noEventsInRange: t(
            "dashboard.widgets.calendar.messages.noEventsInRange",
        ),
        showMore: (total: number) =>
            t("dashboard.widgets.calendar.messages.showMore", { total }),
    };

    // Scrolls the calendar to the current time of day regardless of which date is displayed.
    // Each .rbc-timeslot-group represents one hour, so we locate the group for the current
    // hour and interpolate within it using the current minutes.
    const scrollToCurrentTime = () => {
        const container = document.querySelector(".rbc-time-content");
        if (!container) {
            return;
        }

        const now = new Date();
        const hourGroup = container.querySelectorAll(".rbc-timeslot-group")[
            now.getHours()
        ] as HTMLElement | undefined;
        if (!hourGroup) {
            return;
        }

        container.scrollTo({
            top: Math.max(
                0,
                hourGroup.offsetTop +
                    (now.getMinutes() / 60) * hourGroup.offsetHeight -
                    20,
            ),
            behavior: "smooth",
        });
    };

    useEffect(() => {
        if (agendaEvents && !hasScrolledToCurrentTime.current) {
            scrollToCurrentTime();
            hasScrolledToCurrentTime.current = true;
        }
    }, [agendaEvents]);

    const handleExpand = () => {
        onExpand();
        setCurrentView(Views.WEEK);
        setTimeout(scrollToCurrentTime, 100);
    };

    const handleCollapse = () => {
        onCollapse();
        setOpenedEvent(undefined);
        setCurrentView(Views.DAY);
        setTimeout(scrollToCurrentTime, 100);
    };

    return (
        <ExpandableCard
            className="relative z-overlay flex h-90 flex-col"
            expandedClassName="w-[70vw]"
            isExpanded={isExpanded}
            icon={CalendarIcon}
            onCollapse={handleCollapse}
            onExpand={handleExpand}
            title={t("dashboard.widgets.calendar.title")}
        >
            <div className="relative h-full w-full">
                <Calendar
                    allDayAccessor={(event) => event.isAllDayEvent()}
                    components={{
                        event: (event: EventProps<AgendaItem>) => (
                            <CalendarEventCard event={event} />
                        ),
                    }}
                    culture={i18n.language}
                    endAccessor={(event) => event.end}
                    events={agendaEvents}
                    formats={{
                        dayHeaderFormat:
                            i18n.language === Language.FRENCH
                                ? "EEEE dd MMMM"
                                : "EEEE MMMM dd",
                    }}
                    date={calendarDate}
                    localizer={fnsLocalizer}
                    messages={calendarMessages}
                    onNavigate={(date) => {
                        // AB#1806 — only declare which date is displayed. useGetAgendaEvents
                        // fetches whatever pages are needed to cover it; driving
                        // fetchNextPage() from here raced with the in-flight page and left
                        // the displayed week empty.
                        setCalendarDate(date);

                        const today = new Date();
                        if (
                            date.getFullYear() === today.getFullYear() &&
                            date.getMonth() === today.getMonth() &&
                            date.getDate() === today.getDate()
                        ) {
                            setTimeout(scrollToCurrentTime, 100);
                        }
                    }}
                    onSelectEvent={(event) => {
                        setTimeout(() => {
                            setOpenedEvent(new AgendaItem(event));
                        }, 0); // Allow to set openedEvent after the "click outside" event is triggered
                    }}
                    onSelectSlot={(slotInfo) => {
                        if (openedEvent) {
                            setOpenedEvent(undefined);

                            return;
                        }

                        setOpenedEvent(
                            new AgendaItem({
                                start: slotInfo.start,
                                end: slotInfo.end,
                                subject: "",
                                id: "",
                                attendees: [],
                                categories: [],
                                isAllDay: false,
                                isCancelled: false,
                                showAs: "busy",
                                type: "singleInstance",
                                webLink: "",
                                createdDateTime: new Date(),
                                lastModifiedDateTime: new Date(),
                                importance: "normal",
                                sensitivity: "normal",
                                organizer: new AgendaItemOrganizer({
                                    emailAddress: new AgendaEmailAddress({
                                        name: user?.profile?.displayName ?? "",
                                        address: user?.profile?.email ?? "",
                                    }),
                                }),
                                onlineMeeting: undefined,
                            }),
                        );
                    }}
                    onView={(view) => {
                        setCurrentView(view);
                        setTimeout(scrollToCurrentTime, 100);
                    }}
                    startAccessor={(event) => event.start}
                    step={15}
                    timeslots={4}
                    titleAccessor={(event) => event.subject}
                    view={currentView}
                    views={[Views.WEEK, Views.DAY]}
                    selectable
                    showMultiDayTimes
                />
                <EventModal editionEnabled />
            </div>
        </ExpandableCard>
    );
};

export default DashboardCalendar;
